import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { withTranslation, useTranslation } from "react-i18next";
import { isEmpty } from "lodash";
import { usePreCheckInContext } from "./contexts/PreCheckInContext";
import IDSVG from "../../assets/precheckin/id.svg";
import SelfieSVG from "../../assets/precheckin/selfie.svg";
import SignatureSVG from "../../assets/precheckin/signature.svg";

const GuestConfirmDataCollection = ({ match }) => {
  const {
    propertyUid,
    bookingNumber,
    lastName,
    userEmail,
    language,
    data: { presets, booking, guests, guestService },
    components: { PreCheckInHeader, PreCheckInContainer },
    func: { updateGuests },
  } = usePreCheckInContext();

  const history = useHistory();
  const { t } = useTranslation();
  const [isPageMounted, setIsPageMounted] = useState(true);
  const [guestNumber, setGuestNumber] = useState(1);
  const [currentGuest, setCurrentGuest] = useState(null);

  const goEdit = useCallback(() => {
    history.push(
      `/pre-checkin/${propertyUid}/book-detail/${bookingNumber}/guests/${guestNumber}/data-collection?language=${language}&email=${encodeURIComponent(
        userEmail
      )}`
    );
  }, [bookingNumber, guestNumber, history, language, propertyUid, userEmail]);

  useEffect(() => {
    const { guestNumber: matchedGuestNumber } = match.params;
    setGuestNumber(matchedGuestNumber);

    if (guests && isPageMounted) {
      setIsPageMounted(false);
      const currGuest = guests.allGuests()[matchedGuestNumber - 1];
      setCurrentGuest(currGuest);
    }
  }, [match.params, propertyUid, isPageMounted, guests, goEdit]);

  const goToBookingDetail = () => {
    history.push(
      `/pre-checkin/${propertyUid}/book-detail/${bookingNumber}${
        lastName ? `/${encodeURIComponent(lastName)}` : ""
      }?language=${language}`
    );
  };

  const goToNextStep = () => {
    guests.guests[guestNumber - 1].completed = true;
    updateGuests(guests);
    goToBookingDetail();
  };

  const goToRestartIdentify = () => {
    guests.guests[guestNumber - 1].clientSecret = null;
    guests.guests[guestNumber - 1].stripeSubmitted = false;
    guests.guests[guestNumber - 1].verified = false;
    guests.guests[guestNumber - 1].completed = false;
    updateGuests(guests);
    goToBookingDetail();
  };

  return (
    <>
      <PreCheckInContainer containerName="guest-confirm-data-collection">
        <PreCheckInHeader
          title={t("CONFIRM")}
          headerLink
        />
        {booking && !isEmpty(currentGuest) && (
          <>
            <section
              id="content"
              className="relative flex flex-col px-9 w-full flex-1 md:flex-0 pt-6">
              <div className="md:max-w-7xl mx-auto w-full">
                <div className="flex flex-col justify-center pt-5">
                  <div className="mb-8">
                    <p
                      className="type-regular medium pb-6"
                      style={{ borderBottom: "1px #C1C1C1 solid" }}>
                      {t(
                        "CONFIRM_YOUR_DETAILS",
                        "Please confirm your details below."
                      )}
                    </p>
                  </div>
                  <div
                    className="mb-6 pb-6 text-center"
                    style={{ borderBottom: "1px #C1C1C1 solid" }}>
                    <div className="flex flex-wrap mb-6 justify-between items-center">
                      <label className="text-okkami-hus type-title small">
                        {t("FULL_NAME", "Full Name")}
                      </label>
                      <p
                        className="text-okkami-hus type-title medium"
                        style={{ color: presets.backgroundColorTopBar }}>
                        {currentGuest.title} {currentGuest.firstName}{" "}
                        {currentGuest.lastName}
                      </p>
                    </div>

                    <div className="flex flex-wrap mb-6 justify-between items-center">
                      <label className="text-okkami-hus type-title small">
                        {t("EMAIL")}
                      </label>
                      <p
                        className="text-okkami-hus type-title medium"
                        style={{ color: presets.backgroundColorTopBar }}>
                        {currentGuest.email}
                      </p>
                    </div>

                    <div className="flex flex-wrap mb-6 justify-between items-center">
                      <label className="text-okkami-hus type-title small">
                        {t("MOBILE_NUMBER", "Mobile Number")}
                      </label>
                      <p
                        className="text-okkami-hus type-title medium"
                        style={{ color: presets.backgroundColorTopBar }}>
                        {currentGuest.phoneCode} {currentGuest.phone}
                      </p>
                    </div>

                    {guestService.enableDateOfBirth && (
                      <div className="flex flex-wrap mb-6 justify-between items-center">
                        <label className="text-okkami-hus type-title small">
                          {t("DATE_OF_BIRTH", "Date Of Birth")}
                        </label>
                        <p
                          className="text-okkami-hus type-title medium"
                          style={{ color: presets.backgroundColorTopBar }}>
                          {currentGuest.birthday}
                        </p>
                      </div>
                    )}

                    <div className="flex flex-wrap mb-6 justify-between items-center">
                      <label className="text-okkami-hus type-title small">
                        {t("COUNTRY_OF_RESIDENCE", "Country Of Residence")}
                      </label>
                      <p
                        className="text-okkami-hus type-title medium"
                        style={{ color: presets.backgroundColorTopBar }}>
                        {currentGuest.country}
                      </p>
                    </div>

                    <div className="flex flex-wrap mb-6 justify-between items-center">
                      <label className="text-okkami-hus type-title small">
                        {t("NATIONALITY")}
                      </label>
                      <p
                        className="text-okkami-hus type-title medium"
                        style={{ color: presets.backgroundColorTopBar }}>
                        {currentGuest.nationality}
                      </p>
                    </div>

                    <div className="flex flex-wrap mb-6 justify-between items-center">
                      <label className="text-okkami-hus type-title small">
                        {t("ID_TYPE", "ID TYPE")}
                      </label>
                      <p
                        className="text-okkami-hus type-title medium capitalize"
                        style={{ color: presets.backgroundColorTopBar }}>
                        {currentGuest.idType.replace("_", " ")}
                      </p>
                    </div>

                    <div className="flex flex-wrap mb-6 justify-between items-center">
                      <label className="text-okkami-hus type-title small">
                        {t("ID_OR_NATIONAL_NUMBER", "ID / National Number")}
                      </label>
                      <p
                        className="text-okkami-hus type-title medium"
                        style={{ color: presets.backgroundColorTopBar }}>
                        {currentGuest.idType !== "passport" &&
                          currentGuest.passport}
                      </p>
                    </div>

                    <div className="flex flex-wrap mb-6 justify-between items-center">
                      <label className="text-okkami-hus type-title small">
                        {t("PASSPORT_NUMBER", "Passport Number")}
                      </label>
                      <p
                        className="text-okkami-hus type-title medium"
                        style={{ color: presets.backgroundColorTopBar }}>
                        {currentGuest.idType === "passport" &&
                          currentGuest.passport}
                      </p>
                    </div>

                    <button
                      className={`py-4 uppercase text-white type-title medium max-w-md text-center mx-auto w-full mb-4 ${presets.buttonStyleName}`}
                      style={{
                        backgroundColor: presets.backgroundColorTopBar,
                        color: presets.appHomeTextCallToAction,
                      }}
                      onClick={() => goEdit()}>
                      {t("EDIT")}
                    </button>
                  </div>

                  <div className="text-center my-6">
                    <div className="flex justify-center flex-wrap mb-12">
                      <div className="flex flex-col rounded-xl w-64 mb-4">
                        <div
                          className="flex justify-center rounded-xl h-64 p-14 mb-2 mr-4 relative"
                          style={{ border: "1px #C1C1C1 solid" }}>
                          <div className="absolute top-2 right-2">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-8 w-8 bg-white rounded-full"
                              viewBox="0 0 512 512"
                              fill="#32C33D"
                              stroke="#fff">
                              <path
                                fill="#32C33D"
                                d="M504 256c0 136.967-111.033 248-248 248S8 392.967 8 256 119.033 8 256 8s248 111.033 248 248zM227.314 387.314l184-184c6.248-6.248 6.248-16.379 0-22.627l-22.627-22.627c-6.248-6.249-16.379-6.249-22.628 0L216 308.118l-70.059-70.059c-6.248-6.248-16.379-6.248-22.628 0l-22.627 22.627c-6.248 6.248-6.248 16.379 0 22.627l104 104c6.249 6.249 16.379 6.249 22.628.001z"></path>
                            </svg>
                          </div>
                          <img
                            className="text-center"
                            src={SelfieSVG}
                            alt={t("SELFIE_PHOTO", "Selfie Photo")}
                          />
                        </div>
                        <p>{t("SELFIE_PHOTO", "Selfie Photo")}</p>
                      </div>

                      {guestService.enableSignature && (
                        <div className="flex flex-col rounded-xl w-64 mb-2">
                          <div
                            className="flex justify-center rounded-xl h-64 p-14 mb-2 mr-4 relative"
                            style={{ border: "1px #C1C1C1 solid" }}>
                            <div className="absolute top-2 right-2">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-8 w-8 bg-white rounded-full"
                                viewBox="0 0 512 512"
                                fill="#32C33D"
                                stroke="#fff">
                                <path
                                  fill="#32C33D"
                                  d="M504 256c0 136.967-111.033 248-248 248S8 392.967 8 256 119.033 8 256 8s248 111.033 248 248zM227.314 387.314l184-184c6.248-6.248 6.248-16.379 0-22.627l-22.627-22.627c-6.248-6.249-16.379-6.249-22.628 0L216 308.118l-70.059-70.059c-6.248-6.248-16.379-6.248-22.628 0l-22.627 22.627c-6.248 6.248-6.248 16.379 0 22.627l104 104c6.249 6.249 16.379 6.249 22.628.001z"></path>
                              </svg>
                            </div>
                            <img
                              className="text-center"
                              src={SignatureSVG}
                              alt={t("SIGNATURE")}
                            />
                          </div>
                          <p>{t("SIGNATURE")}</p>
                        </div>
                      )}
                      <div className="flex flex-col rounded-xl w-64 mb-4">
                        <div
                          className="flex justify-center rounded-xl h-64 p-14 mb-2 mr-4 relative"
                          style={{ border: "1px #C1C1C1 solid" }}>
                          <div className="absolute top-2 right-2">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-8 w-8 bg-white rounded-full"
                              viewBox="0 0 512 512"
                              fill="#32C33D"
                              stroke="#fff">
                              <path
                                fill="#32C33D"
                                d="M504 256c0 136.967-111.033 248-248 248S8 392.967 8 256 119.033 8 256 8s248 111.033 248 248zM227.314 387.314l184-184c6.248-6.248 6.248-16.379 0-22.627l-22.627-22.627c-6.248-6.249-16.379-6.249-22.628 0L216 308.118l-70.059-70.059c-6.248-6.248-16.379-6.248-22.628 0l-22.627 22.627c-6.248 6.248-6.248 16.379 0 22.627l104 104c6.249 6.249 16.379 6.249 22.628.001z"></path>
                            </svg>
                          </div>
                          <img
                            className="text-center"
                            src={IDSVG}
                            alt={t("ID_PHOTO", "ID Photo")}
                          />
                        </div>
                        <p>{t("ID_PHOTO", "ID Photo")}</p>
                      </div>
                      <div className="flex justify-center p-6 rounded-xl w-64 h-64 p-14 mb-4 mr-4 md:hidden"></div>
                    </div>
                    <button
                      className={`py-4 uppercase text-white type-title medium max-w-md text-center mx-auto w-full mb-4 ${presets.buttonStyleName}`}
                      style={{
                        backgroundColor: presets.backgroundColorTopBar,
                        color: presets.appHomeTextCallToAction,
                      }}
                      onClick={() => goToRestartIdentify()}>
                      {t("RESTART_IDENTITY_VERIFICATION")}
                    </button>
                  </div>
                </div>
                <button
                  className={`app-button-ipad type-title medium ${presets.buttonStyleName}`}
                  style={{
                    backgroundColor: presets.backgroundColorTopBar,
                    color: presets.appHomeTextCallToAction,
                  }}
                  onClick={() => goToNextStep()}>
                  {t("CONTINUE")}
                </button>
              </div>
            </section>
            <button
              className="app-button-mobile type-title medium "
              style={{
                backgroundColor: presets.backgroundColorTopBar,
                color: presets.appHomeTextCallToAction,
                position: "fixed",
                bottom: 0,
                left: 0,
              }}
              onClick={() => goToNextStep()}>
              {t("CONTINUE")}
            </button>
          </>
        )}
      </PreCheckInContainer>
    </>
  );
};

export default withTranslation()(GuestConfirmDataCollection);
